import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';

import { CharacterSizeLimits } from '@app/eds';
import { ITextAreaConfig, SharedConstants, SharedCSSPropertyName } from '@app/eds';

@Component({
  selector: 'app-textarea[placeholderText][textareaHeight][keyup]',
  templateUrl: './textarea.component.html',
  styleUrls: ['./textarea.component.scss']
})
export class TextareaComponent implements OnInit, OnChanges {
  // placeholderText provides the textarea placeholder
  @Input() placeholderText!: string;

  // provides width and height like textareaWidth, textareaHeight
  @Input() textareaWidth!: string;
  @Input() textareaHeight!: string;
  @Input() defValue: string = SharedConstants.EMPTY_STRING;

  // to mandate textbox content or not
  @Input() isRequired = false;

  // whether to show custom error msg or not
  @Input() hasCustomError = false;

  // custom error msg to show
  @Input() customErrorMsg: string = SharedConstants.EMPTY_STRING;

  // FormControl value for textarea input field.
  @Input() inputText = new FormControl(SharedConstants.EMPTY_STRING);

  // characterLimit provides maxLength of inputText
  @Input() characterLimit: number = CharacterSizeLimits.DEFAULT_LIMIT;

  @Input() commentValidatorConfig!: ITextAreaConfig;
  // soft maxLength of inputText
  @Input() softMaxLength: number = CharacterSizeLimits.DEFAULT_LIMIT;

  // provide custom class for textarea
  @Input() customTextAreaClass: string = SharedConstants.EMPTY_STRING;

  // error message shown for required.
  @Input() requiredErrorMessage = 'shared.inputField.requiredMessage';

  // This function will emit the event.target.value on pressing enter key.
  @Output() byEnterPress: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

  // This function will emit the event.target.value on key press.
  @Output() byKeyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes) {
      return;
    }
    if (changes['commentValidatorConfig']) {
      this.inputText.updateValueAndValidity();
      this.inputText.markAllAsTouched();
    }
  }

  ngOnInit() {
    if (this.defValue !== SharedConstants.EMPTY_STRING) {
      this.inputText.setValue(this.defValue);
    }
  }

  /**
   * This method will help to return styles for form.
   *
   * @returns {object} styles
   */
  getFormStyles = () => {
    const formStylingObj: { [key: string]: string } = {};

    this.textareaWidth &&
      (formStylingObj[SharedCSSPropertyName.WIDTH] = this.textareaWidth);

    return formStylingObj;
  };

  /**
   * This method will help to return styles for input textarea.
   *
   * @returns {object} styles
   */
  getInputStyles = () => {
    const inputStylingObj: { [key: string]: string } = {};

    this.textareaHeight &&
      (inputStylingObj[SharedCSSPropertyName.HEIGHT] = this.textareaHeight);

    return inputStylingObj;
  };

  /**
   * This function will call on clicking cancel.
   * It will clear the textarea's inputText.
   */
  onClear = () => this.inputText.setValue(SharedConstants.EMPTY_STRING);

  /**
   * This function will call after add comment successfuly.
   * It will clear the textarea's error.
   */
  onResetFormControl = () => {
    this.inputText.reset();
    this.inputText.markAsUntouched();
  };

  /**
   * This function will trigger on pressing the enter key.
   * It will bind the event.target.value to the inputText
   *
   * @param {event}
   */
  onKeyEnter = (event: KeyboardEvent) => this.byEnterPress.emit(event);

  /**
   * This function will trigger on Key Up.
   *
   * @param {event}
   */
  onKeyUp = (event: KeyboardEvent) => this.byKeyup.emit(event);

  /**
   * Checks whether to show invalid input error or not
   * only if there is no error in maxlength, min and max,
   * this error will be shown to prevent multiple errors
   * display in the UI.
   *
   * @returns {boolean} bool
   */
  shouldShowInvalidFormatError = (): boolean =>
    this.inputText.value?.length !== this.characterLimit && !this.hasCustomError;
}
